import { isNil, isNull, isUndefined } from 'lodash';
import { all, create, EvalFunction, MathNode } from 'mathjs';
import { mathGlobalScope } from 'common/constants';
import { GRID_DATE, GRID_NUMBER_CHECKBOX, LEDGER, PERCENTAGE, STRING } from 'common/constants/gridType';
import * as customFormulas from 'common/formulas/math.js';
import { getGetSymbolSet } from 'components/ScalarSpreadsheet/utilities/mathUtil';
import {
  CellValue,
  EvaluatedCell,
  EvaluatedCellMap,
  EvaluatedCellSheetMap,
  ICell,
  IScalarSpreadsheetConfig,
  ScalarSpreadsheetConfigDataInterface
} from 'models/interfaces/spreadsheet-types/core-types';
import { formatNumbers, Logger, toString } from 'utilities';
import { shouldAddCurrencySymbol } from 'utilities/computeCellChanges';
import { GridType } from '../../../common/types/scalarSpreadsheet';

const math = create(all);
math.import(customFormulas);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export class Cell<CD extends object = object, I extends object = object, COLUMNS extends object = object, TD extends object = object> implements EvaluatedCell<CD, I, COLUMNS, TD> {
  alias: string;

  backgroundColor?: string;

  code?: EvalFunction;

  columnLegend: string;

  currency?: { currencyCode: string };

  customKey?: string;

  dbDecimalPlaces?: number;

  defaultValue?: any;

  defaultZero?: boolean;

  dependencies: Set<EvaluatedCell>;

  displayUndefined?: boolean;

  enabled?: boolean | undefined;

  evaluated: boolean;

  expr: any;

  format?: any;

  gridType?: GridType | null;

  invalidExpr?: boolean;

  isExpr?: boolean;

  isTotal?: boolean;

  key: string;

  linkedCells: Set<EvaluatedCell>;

  linkedCellSymbols?: Set<string>;

  maxNumberDigits?: number;

  pairCell?: EvaluatedCell;

  providers: Set<EvaluatedCell>;

  rowNumber: number;

  sheet: ScalarSpreadsheetConfigDataInterface<I, COLUMNS, TD, CD>;

  skipIfNullProviders?: boolean;

  symbols?: Set<string>;

  totalKey?: string;

  useFormulaValue?: boolean;

  useValueIfInvalidExpr?: boolean;

  value?: any;

  x: number;

  y: number;

  constructor(expr: CellValue, key: string, x: number, y: number, sheet: ScalarSpreadsheetConfigDataInterface<I, COLUMNS, TD, CD>, rowNumber: number, columnLegend: string) {
    this.alias = '';
    this.expr = expr;
    this.value = undefined;
    this.dependencies = new Set();
    this.evaluated = false;
    this.providers = new Set();
    this.linkedCells = new Set();
    this.x = x;
    this.y = y;
    this.key = key;
    this.sheet = sheet;
    this.columnLegend = columnLegend;
    this.rowNumber = rowNumber;
  }

  setXY(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  setKey(key: string, sheet: ScalarSpreadsheetConfigDataInterface<I, COLUMNS, TD, CD>) {
    this.key = key;
    this.sheet = sheet;
  }

  setNotExpr() {
    this.value = this.expr;
    this.isExpr = false;
  }

  setIsExpr(code: EvalFunction) {
    this.isExpr = true;
    this.evaluated = false;
    this.code = code;
  }

  setExpr(isExpr: boolean, code: MathNode) {
    this.isExpr = isExpr;
    if (isExpr === false) {
      this.setNotExpr();
    } else {
      this.setIsExpr(code);
    }
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  setValue(value: typeof this.value) {
    if (!this.displayUndefined && (Number.isNaN(value) || isUndefined(value)) && isUndefined(this.totalKey)) {
      this.value = 0;
    } else {
      this.value = value;
    }
    this.evaluated = true;
  }

  getFullKey(): string {
    if (!this.sheet?.name || !this.key) {
      return '';
    }
    let { key } = this;

    if (this.totalKey) {
      key = this.totalKey;
    }
    return `${this.sheet.name}.${key}`;
  }

  initializeLinkedCells(sheet: EvaluatedCellMap<CD, I, COLUMNS, TD>, scope: EvaluatedCellSheetMap) {
    const symbols = this.linkedCellSymbols || new Set();
    const arrayData = Array.from(symbols);
    const linkedCells = new Set<EvaluatedCell>();
    arrayData.forEach(symbol => {
      if (symbol.indexOf('.') >= 0) {
        const [sheetName, key] = symbol.split('.');
        linkedCells.add(scope[sheetName][key]);
      } else if (sheet?.[symbol]) {
        // this is a result of overly strict typescript checking - sheet[symbol] is this particular sheet, and its generics all extend object
        const sheetCell  = sheet[symbol];
        linkedCells.add(sheetCell as unknown as EvaluatedCell);
      } else if (isUndefined(scope[symbol])) {
        // undefined symbol:
        throw new Error(`Undefined symbol: ${symbol}`);
      }
    });
    this.linkedCells = linkedCells;
  }

  getKeys() {
    if (this.customKey) {
      return [this.customKey, this.key];
    }
    return [this.key];
  }

  getRelativeKeys(sheet: IScalarSpreadsheetConfig) {
    if (this.sheet?.name === sheet.name) {
      return this.getKeys();
    }
    return this.getKeys().map(key => `${this.sheet?.name}.${key}`);
  }

  initializeDependencies(symbols: Set<string>, sheet: EvaluatedCellMap, scope: EvaluatedCellSheetMap) {
    const arrayData = Array.from(symbols);
    const providers = new Set<EvaluatedCell>();
    arrayData.forEach(symbol => {
      if (symbol.indexOf('.') >= 0) {
        const [sheetName, key] = symbol.split('.');
        scope[sheetName][key].dependencies.add(this as unknown as EvaluatedCell);
        providers.add(scope[sheetName][key]);
      } else if (sheet?.[symbol]) {
        sheet[symbol].dependencies.add(this as unknown as EvaluatedCell);
        providers.add(sheet[symbol]);
      } else if (isUndefined(scope[symbol])) {
        // undefined symbol:
        throw new Error(`Undefined symbol: ${symbol}`);
      }
    });
    this.symbols = symbols;
    this.providers = providers;
  }

  getExprString() {
    if (!isNil(this.expr) && this.expr !== '') {
      return this.expr.toString();
    }
    return '';
  }

  getSymbols() {
    const strValue = this.getExprString();
    if (!strValue || strValue.length <= 1) {
      return new Set<string>();
    }

    try {
      const node = math.parse(strValue.substring(1));
      const symbols = getGetSymbolSet(node);
      return symbols;
    } catch {
      // Handle parsing errors gracefully
      Logger.error('Error parsing expression in getSymbols', {
        sheet: this.sheet?.name,
        key: this.key,
        expr: this.expr,
      });
      return new Set<string>();
    }
  }

  initializeCellValue(sheet: EvaluatedCellMap<CD, I, COLUMNS, TD>, cellScope: EvaluatedCellSheetMap) {
    const strValue = this.getExprString();
    this.invalidExpr = false;
    const scope = {
      ...cellScope,
      ...mathGlobalScope,
    } as unknown as EvaluatedCellSheetMap;
    this.initializeLinkedCells(sheet, scope);
    if (strValue[0] === '=') {
      this.isExpr = true;
      try {
        const node = math.parse(strValue.substring(1));
        const symbols: Set<string> = getGetSymbolSet(node);
        this.initializeDependencies(symbols, sheet as unknown as EvaluatedCellMap, scope);
        this.setIsExpr(node.compile());
      } catch {
        if (this.useValueIfInvalidExpr) {
          this.expr = this.value;
          this.isExpr = false;
        } else if (this.defaultZero) {
          this.expr = 0;
          this.setNotExpr();
          Logger.error('Setting default zero', {
            sheet: this.sheet?.name,
            key: this.key,
            expr: this.expr,
          });
        } else {
          this.invalidExpr = true;
          Logger.error(`Cannot initialize expression ${strValue} on cell ${this.sheet?.name}.${this.key}`, {
            sheet: this.sheet?.name,
            key: this.key,
            expr: this.expr,
          });
        }
      }
    } else {
      this.setNotExpr();
    }
  }

  getExprScope() {
    return Array.from(this.providers).reduce(
      (sofar: { [key: string]: (CellValue | {[key: string]: CellValue}) }, provider: EvaluatedCell<any, any, any, any>) => {
        if (provider.sheet === this.sheet) {
          // eslint-disable-next-line no-param-reassign
          sofar[provider.key as string] = provider.parseCellValue && provider.parseCellValue();
          if (provider.customKey) {
            // eslint-disable-next-line no-param-reassign
            sofar[provider.customKey] = sofar[provider.key as string];
          }
        } else {
          const providerSheetName = provider.sheet.name;
          if (isUndefined(sofar[providerSheetName])) {
            // eslint-disable-next-line no-param-reassign
            sofar[providerSheetName] = {};
          }
          const cellValue = provider.parseCellValue && provider.parseCellValue();
          const sheetScope = sofar[providerSheetName] as {[key: string]: CellValue};

          if (!provider.isTotal) {
            sheetScope[provider.key] = cellValue;
            if (provider.customKey) {
              sheetScope[provider.customKey] = cellValue;
            }
          } else {
            sheetScope[`TOTALS_${provider.key}`] = cellValue;
            sheetScope[`TOTALS_${provider.alias}`] = cellValue;
          }
        }
        return sofar;
      },
      { ...mathGlobalScope }
    );
  }

  updateDependencies(startNode: EvaluatedCell) {
    return Array.from(this.dependencies)
      .flatMap(dependency => {
        if (dependency.calcValue) {
          return dependency.calcValue(startNode);
        }
        return null;
      })
      .filter(result => !!result);
  }

  getLinkedCells() {
    if (this.linkedCells) {
      const response = [
        this,
        ...Array.from(this.linkedCells).flatMap(linkedCell => linkedCell?.getLinkedCells?.()),
      ];
      return response as (EvaluatedCell)[];
    }
    return [];
  }

  setCellBackgroundColor(color?: string) {
    this.backgroundColor = color;
  }

  setDisplayConfig(tableConfig: { [key: string]: any } = {}, cellConfig: { [key: string]: any } = {}) {
    Object.entries(tableConfig).forEach(([key, value]) => {
      const hasTableConfig = Object.keys(cellConfig).includes(key);
      const hasCellConfig = Object.keys(cellConfig).includes(key);
      this[key as keyof this] = hasTableConfig || hasCellConfig ? { ...tableConfig[key], ...cellConfig[key] } : value;
    });
  }

  parseCellValue(): CellValue {
    if (this.isTotal && !this.useFormulaValue) {
      return shouldAddCurrencySymbol(this.value)
        ? formatNumbers({
          format: this.format,
          currencyCode: this.currency?.currencyCode ?? null,
          value: this.value,
        } as any)
        : this.value;
    }
    if (this.gridType === GRID_DATE) {
      return this.value;
    }
    if (this.gridType === GRID_NUMBER_CHECKBOX) {
      return this;
    }
    if (this.gridType === STRING) {
      return this.value;
    }
    if (this.gridType === LEDGER) {
      return this.value;
    }
    // eslint-disable-next-line no-restricted-globals
    if (isUndefined(this) || isNaN(this.value)) {
      return 0;
    }
    if (this.gridType === PERCENTAGE) {
      return parseFloat(`${Number(this.value)}`);
    }
    return parseFloat(`${Number(this.value)}`);
  }

  fitValue(value: CellValue) {
    // make sure we know what type we are working with
    let tmpValue = value ? value.toString() : value;
    if (
      value
      && this.maxNumberDigits
      && this.isExpr
      && value.toString().includes('.')
      && value.toString().length > this.maxNumberDigits
    ) {
      tmpValue = Number(Number(value).toFixed(this.maxNumberDigits - value.toString().split('.')[0].length));
    }
    if (value && this.dbDecimalPlaces) {
      tmpValue = Number(Number(tmpValue).toFixed(this.dbDecimalPlaces));
    }
    if (isNull(value)) {
      return value;
    }
    if (tmpValue === Infinity || Number.isNaN(tmpValue)) {
      tmpValue = 0;
    }
    return tmpValue;
  }

  providersAreEmpty() {
    let providersList = Array.from(this.providers);
    if (this.symbols?.size === 0 && !isUndefined(this.defaultValue)) {
      // Expression will evaluate on false
      return false;
    }
    providersList = providersList.filter(c => isUndefined(c.enabled) || c.enabled);
    if (providersList.length === 0 && this.symbols?.size === 0) {
      return false;
    }
    if (providersList.length === 0) {
      // true will  cause the cell to get the default value
      return true;
    }
    return providersList.every(c => !toString(c.value).length);
  }

  setProps(props: ICell) {
    Object.entries(props).forEach(([key, value]) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this[key] = value;
    });
  }

  calcValue(startNode?: EvaluatedCell<CD, I, COLUMNS, TD>) {
    let sNode = startNode;
    if (startNode === this) {
      return null;
    }
    if (!startNode) {
      sNode = this;
    }
    if (!sNode) {
      return null;
    }

    const getDefaultValue = () => (!isUndefined(this.defaultValue) ? this.defaultValue : null);
    const providerCheckCallback = (provider: EvaluatedCell) =>
      this.skipIfNullProviders ? provider.evaluated && provider.value !== null : provider.evaluated;

    if (this.isExpr && Array.from(this.providers).every(provider => providerCheckCallback(provider))) {
      try {
        const exprScope = this.getExprScope();

        // If all cells providers are empty, then is not necessary to evaluate the expression
        let val = null;
        try {
          val = this.providersAreEmpty() ? getDefaultValue() : this.code?.evaluate(exprScope);
        } catch {
          val = null;
          Logger.error('Error evaluating expression', {
            sheet: this.sheet?.name,
            key: this.key,
            expr: this.expr,
          });
        }
        const fittedValue = this.fitValue(val);
        this.setValue(fittedValue);

        const changes = this.updateDependencies(sNode as unknown as EvaluatedCell);
        return [this as unknown as EvaluatedCell, ...changes];
      } catch (e: any) {
        if (this.sheet.ignoreCellExpressionErrors) {
          Logger.debug(`Ignoring error evaluating expression on cell: ${this.sheet?.name} ${this.key}: ${e.toString()}`);
          this.setValue(null);
        } else {
          throw new Error(`Error updating dependencies on cell: ${this.sheet?.name} ${this.key}: ${e.toString()}`);
        }
      }
    }
    if (!this.isExpr && this.dependencies) {
      this.setValue(this.expr);
      const changes = this.updateDependencies(sNode as unknown as EvaluatedCell);
      return [this as unknown as EvaluatedCell, ...changes];
    }
    return null;
  }

  removeDependencies() {
    if (this.value?.[0] === '=') {
      Array.from(this.dependencies).forEach(node => {
        node.removeDependencies && node.removeDependencies();
      });
      this.dependencies = new Set();
    }
  }

  unmarkDependencies(startNode?: EvaluatedCell) {
    let sNode = startNode;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (sNode === this) {
      return;
    }
    if (!sNode) {

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      sNode = this;
    }
    this.evaluated = false;
    Array.from(this.dependencies).forEach(
      dependency => dependency.unmarkDependencies && dependency.unmarkDependencies(sNode)
    );
  }

  resetCell(updatedCell: EvaluatedCell) {
    Array.from(this.dependencies).forEach(dependency => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      dependency.providers.delete(this);
      dependency.providers.add(updatedCell);
    });
  }

  dispatchEvent() {
    if (this.sheet && this.key) {
      const event = new Event(this.getFullKey(), { bubbles: true, cancelable: true });
      document.dispatchEvent(event);
    }
  }

  removePairCell() {
    if (!this.pairCell) {
      return;
    }
    this.dependencies.delete(this.pairCell);
  }

  onChange(expr: CellValue, scope: EvaluatedCellSheetMap) {
    const sheet = scope[this.sheet?.name as string] as unknown as EvaluatedCellMap<CD, I, COLUMNS, TD>;
    this.expr = expr;
    this.removePairCell();
    this.removeDependencies();
    this.initializeCellValue(sheet, scope);
    this.unmarkDependencies();
    const changes = this.calcValue();
    return changes ? changes.filter(change => change) : [];
  }
}
